












































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import { RootState } from '../../../../store/store';
import { ApiState } from '@/store/types/general.types';
import {
  AddressCountry,
  AddressFilterOption,
  AddressVenueFieldOptionIndexes,
  VenueFieldData
} from '@/store/modules/admin/types/group-level-attribute.types';
import DraggableIcon from './DraggableIcon.vue';
import { clone } from '@/jbi-shared/util/group-level-attributes.util';
import {
  GroupLevelAttributeOption,
  GroupLevelAttributeOptionStatus,
  GroupLevelAttributeValue
} from '@/jbi-shared/types/jaas-group-level-attributes.types';
import AttributeActionButtons from './AttributeActionButtons.vue';
import AddressVenueFields from './components/AddressVenueFields.vue';

@Component({
  components: {
    DraggableIcon,
    AttributeActionButtons,
    AddressVenueFields
  }
})
export default class GroupLevelAttributeAddressField extends Vue {
  @Prop() attributeValue!: GroupLevelAttributeValue;
  @Prop() fieldClasses!: any;
  @Prop() duplicationError!: boolean;
  @Prop({ default: false }) isSettings!: boolean;
  @Prop({ default: false }) overrideDisableDraggable!: boolean;
  @Prop({ default: false }) overrideDisableAttributeButton!: boolean;
  @Prop() deleteAttribute!: () => void;
  @Prop() updateAttributeValue!: (attribute: GroupLevelAttributeValue) => void;
  @Prop() toggleAttributeRequire!: (
    attribute: GroupLevelAttributeValue
  ) => void;
  @Prop() editAttribute!: (attribute: GroupLevelAttributeValue) => void;

  venueHasError: boolean = false;

  localBuildingValue: string = '';
  localLineOneValue: string = '';
  localLineTwoValue: string = '';
  localCityValue: string = '';
  localPostcodeValue: string = '';
  localStateValue: string = '';
  localCountryValue: string = '';

  @Action('admin/getCountries')
  getAllCountries!: (addressFilter?: AddressFilterOption) => void;

  @State(({ admin }: RootState) => admin.apiState.getCountries)
  getAllCountriesApiState!: ApiState;

  @State(({ admin }: RootState) => admin.countries)
  allCountries!: AddressCountry[] | undefined;

  @Action('admin/getCountryByCountryId')
  getCountryById!: (countryId?: number) => void;

  @State(({ admin }: RootState) => admin.apiState.getCountryById)
  getCountryByIdApiState!: ApiState;

  @State(({ admin }: RootState) => admin.countryById)
  countryById!: AddressCountry | undefined;

  mounted() {
    if (this.allCountries === undefined) {
      this.getAllCountries();
    }

    if (this.isSettings) {
      this.syncPropToLocalData();
    }
  }

  get venueData(): VenueFieldData {
    return {
      building: this.localBuildingValue,
      lineOne: this.localLineOneValue,
      lineTwo: this.localLineTwoValue,
      city: this.localCityValue,
      postcode: this.localPostcodeValue,
      country: this.localCountryValue,
      state: this.localStateValue
    };
  }
  set venueData(newValue: VenueFieldData) {
    this.localBuildingValue = newValue.building;
    this.localLineOneValue = newValue.lineOne;
    this.localLineTwoValue = newValue.lineTwo;
    this.localCityValue = newValue.city;
    this.localPostcodeValue = newValue.postcode;
    this.localCountryValue = newValue.country;
    this.localStateValue = newValue.state;
  }

  get attributeValueAsGLAOptionArray(): GroupLevelAttributeOption[] {
    if (!this.attributeValue.value) {
      return this.attributeValue.groupLevelAttributeType
        .option as GroupLevelAttributeOption[];
    }
    return this.attributeValue.value as GroupLevelAttributeOption[];
  }

  // Computed property so changes from the value from parent component are synced
  get isRequired() {
    return this.attributeValue.groupLevelAttributeSpec.isRequired;
  }

  // Address optional field configs
  get attributeOptionConfigs(): GroupLevelAttributeOption[] {
    return this.attributeValue.option as GroupLevelAttributeOption[];
  }

  get buildingOptions() {
    return this.attributeOptionConfigs[AddressVenueFieldOptionIndexes.BUILDING];
  }

  get lineTwoOptions() {
    return this.attributeOptionConfigs[AddressVenueFieldOptionIndexes.LINETWO];
  }

  get shouldEnableBuildingName() {
    return (
      this.buildingOptions.status === GroupLevelAttributeOptionStatus.ACTIVE
    );
  }

  get shouldEnableLineTwo() {
    return (
      this.lineTwoOptions.status === GroupLevelAttributeOptionStatus.ACTIVE
    );
  }

  getAttributeValueAsArray(): GroupLevelAttributeOption[] {
    // Divide by two because of reverse enum mappings
    // https://www.typescriptlang.org/docs/handbook/enums.html#reverse-mappings
    const addressVenueFieldArrayLength: number =
      Object.entries(AddressVenueFieldOptionIndexes).length / 2;

    if (
      Array.isArray(this.attributeValue.value) &&
      this.attributeValue.value.length >= addressVenueFieldArrayLength
    ) {
      return this.attributeValue.value as GroupLevelAttributeOption[];
    } else if (
      Array.isArray(this.attributeValue.groupLevelAttributeType.option)
    ) {
      /**
       * Fallback to default options structure if value is missing/invalid
       * This also ensures the default options have a 'value' property for consistency
       */
      return (this.attributeValue.groupLevelAttributeType
        .option as GroupLevelAttributeOption[]).map((opt) => ({
        ...opt,
        value: ''
      }));
    }
    // Absolute fallback - should ideally not happen if props are correct
    return Array(addressVenueFieldArrayLength).fill({
      value: '',
      placeholder: '',
      status: 'ACTIVE'
    });
  }

  buildAttributeValueFromLocalState(): GroupLevelAttributeValue {
    const updatedValueArray: GroupLevelAttributeOption[] = clone(
      this.getAttributeValueAsArray()
    );

    // Update values from local state
    updatedValueArray[
      AddressVenueFieldOptionIndexes.BUILDING
    ].value = this.localBuildingValue;
    updatedValueArray[
      AddressVenueFieldOptionIndexes.LINEONE
    ].value = this.localLineOneValue;
    updatedValueArray[
      AddressVenueFieldOptionIndexes.LINETWO
    ].value = this.localLineTwoValue;
    updatedValueArray[
      AddressVenueFieldOptionIndexes.CITY
    ].value = this.localCityValue;
    updatedValueArray[
      AddressVenueFieldOptionIndexes.POSTCODE
    ].value = this.localPostcodeValue;
    updatedValueArray[
      AddressVenueFieldOptionIndexes.STATE
    ].value = this.localStateValue;
    updatedValueArray[
      AddressVenueFieldOptionIndexes.COUNTRY
    ].value = this.localCountryValue;

    // Create a new object to emit
    const newAttributeValue: GroupLevelAttributeValue = {
      ...clone(this.attributeValue),
      value: updatedValueArray,
      hasFieldError: this.venueHasError
    };

    return newAttributeValue;
  }

  syncPropToLocalData() {
    const propValueArray = this.getAttributeValueAsArray();

    this.localBuildingValue =
      (propValueArray[AddressVenueFieldOptionIndexes.BUILDING]
        ?.value as string) || '';
    this.localLineOneValue =
      (propValueArray[AddressVenueFieldOptionIndexes.LINEONE]
        ?.value as string) || '';
    this.localLineTwoValue =
      (propValueArray[AddressVenueFieldOptionIndexes.LINETWO]
        ?.value as string) || '';
    this.localCityValue =
      (propValueArray[AddressVenueFieldOptionIndexes.CITY]?.value as string) ||
      '';
    this.localPostcodeValue =
      (propValueArray[AddressVenueFieldOptionIndexes.POSTCODE]
        ?.value as string) || '';
    this.localStateValue =
      (propValueArray[AddressVenueFieldOptionIndexes.STATE]?.value as string) ||
      '';
    this.localCountryValue =
      (propValueArray[AddressVenueFieldOptionIndexes.COUNTRY]
        ?.value as string) || '';
  }

  emitUpdate() {
    const updatedAttribute = this.buildAttributeValueFromLocalState();
    this.updateAttributeValue(updatedAttribute);
  }

  setFieldError(value: boolean) {
    this.attributeValue.hasFieldError = value;
    this.emitUpdate();
  }

  // Watchers for local address field changes to trigger validation and emit updates
  @Watch('localBuildingValue')
  @Watch('localLineOneValue')
  @Watch('localLineTwoValue')
  @Watch('localCityValue')
  @Watch('localPostcodeValue')
  @Watch('localStateValue')
  @Watch('localCountryValue')
  onLocalDataChange() {
    // Emit the updated object
    this.emitUpdate();
  }

  @Watch('venueHasError')
  onIndividualFieldErrorStates(): void {
    this.setFieldError(this.venueHasError);
  }
}
